import { z } from "zod";
import { PointVectorSchema } from "./catalog.js";
import { AgentJobSchema, EngineToolResponseSchema, JobStepSchema, ToolCallTraceSchema } from "./jobs.js";
import { ProviderRequestFingerprintSchema } from "./providerConfig.js";
import { SpecPolicyFlagProposalSchema, SpecPolicyFlagSetSchema } from "./policyFlags.js";
import { EdgeFeatureKindSchema, EdgeProvisionUnitSchema, UiStrategySchema } from "./requirements.js";
import { IdSchema, IsoTimestampSchema, NonEmptyStringSchema, NonNegativeIntegerSchema, PositiveIntegerSchema, requireSourceOrRuleRef, RuleTraceRefSchema, SourceRefSchema, UnknownRecordSchema } from "./source.js";
import { MaterialFieldBasisSchema, ZoneSlotAssignmentSchema } from "./spec.js";
import { RiskTagsObjectSchema, ScopeTagsObjectSchema } from "./tags.js";
export const AgentProposalRationaleSchema = requireSourceOrRuleRef(z
    .object({
    why: NonEmptyStringSchema,
    requirementIds: z.array(IdSchema),
    sourceRefs: z.array(SourceRefSchema),
    ruleTraceRefs: z.array(RuleTraceRefSchema),
    precedentRefs: z.array(SourceRefSchema).optional(),
    notes: z.array(NonEmptyStringSchema).optional()
})
    .strict());
const StrategyElementBaseSchema = z
    .object({
    summary: NonEmptyStringSchema,
    rationale: AgentProposalRationaleSchema
})
    .strict();
export const StrategyScreenTopologyProposalSchema = StrategyElementBaseSchema.extend({
    uiStrategy: UiStrategySchema,
    screenTopology: NonEmptyStringSchema,
    screenKinds: z.array(NonEmptyStringSchema)
}).strict();
export const StrategyMeteringApproachProposalSchema = StrategyElementBaseSchema.extend({
    meteringTopology: NonEmptyStringSchema,
    protocol: NonEmptyStringSchema.optional(),
    meterHardwareRefs: z.array(NonEmptyStringSchema)
}).strict();
export const MeteringInfrastructureRoleSchema = z.enum([
    "by_others_meter",
    "energy_metering_unit",
    "meter_interface_module",
    "metering_gateway",
    "metering_panel_or_enclosure",
    "metering_scope_note"
]);
export const MeteringInfrastructureTopologySchema = z.enum([
    "by_others_or_scope_exclusion",
    "m_bus_cascade",
    "netmaster_direct_modbus",
    "pulse_or_analogue_interface",
    "unknown_needs_decision",
    "wireless_m_bus_cascade"
]);
export const MeteringInfrastructureSupplyScopeSchema = z.enum([
    "by_others",
    "scope_exclusion",
    "third_party_supplied",
    "titan_supplied",
    "unknown_needs_decision"
]);
export const MeteringServiceKindSchema = z.enum([
    "cooling",
    "electric",
    "gas",
    "generic",
    "heat",
    "heat_cool",
    "water"
]);
export const MeteringInfrastructureDetailSchema = z
    .object({
    role: MeteringInfrastructureRoleSchema,
    topology: MeteringInfrastructureTopologySchema,
    supplyScope: MeteringInfrastructureSupplyScopeSchema,
    serviceKinds: z.array(MeteringServiceKindSchema).optional(),
    protocols: z.array(NonEmptyStringSchema).optional(),
    sourceRequirementIds: z.array(IdSchema).optional(),
    notes: z.array(NonEmptyStringSchema).optional()
})
    .strict();
export const StrategyEdgeServicesPostureProposalSchema = StrategyElementBaseSchema.extend({
    posture: NonEmptyStringSchema,
    featureKinds: z.array(EdgeFeatureKindSchema),
    provisionUnit: EdgeProvisionUnitSchema.optional(),
    serviceRefs: z.array(NonEmptyStringSchema)
}).strict();
export const StrategyControlPhilosophyProposalSchema = StrategyElementBaseSchema.extend({
    philosophy: NonEmptyStringSchema,
    controllerTopology: NonEmptyStringSchema,
    fallbackMode: NonEmptyStringSchema.optional()
}).strict();
export const StrategyProposalSchema = z
    .object({
    id: IdSchema,
    projectId: IdSchema,
    apartmentTypeId: IdSchema,
    strategyContextId: IdSchema.optional(),
    uiScreenTopology: StrategyScreenTopologyProposalSchema,
    meteringApproach: StrategyMeteringApproachProposalSchema,
    edgeServicesPosture: StrategyEdgeServicesPostureProposalSchema,
    controlPhilosophy: StrategyControlPhilosophyProposalSchema,
    createdByJobId: IdSchema.optional(),
    proposedAt: IsoTimestampSchema.optional()
})
    .strict();
export const ControllerAllocationProposedRoleSchema = z.enum([
    "fcu_primary",
    "heat_only_710",
    "mz_overflow"
]);
/**
 * B5-W3 prior-decision anchoring: a compact summary of one controller
 * allocation from a previously ACCEPTED decision for the same apartment type,
 * fed forward as context (never a decider) so the agent can choose to keep a
 * stable decision. Deliberately narrower than {@link ControllerAllocationProposalItemSchema}
 * — only the fields that participate in {@link ControllerAllocationProposedRoleSchema}
 * comparisons and the B5 controller decision hash.
 */
export const PriorControllerAllocationSummarySchema = z
    .object({
    role: ControllerAllocationProposedRoleSchema,
    productFamily: NonEmptyStringSchema,
    productPartNo: NonEmptyStringSchema.optional(),
    quantity: PositiveIntegerSchema
})
    .strict();
export const PriorInfrastructureSummarySchema = z
    .object({
    itemKind: NonEmptyStringSchema,
    productFamily: NonEmptyStringSchema.optional(),
    productPartNo: NonEmptyStringSchema.optional(),
    quantity: PositiveIntegerSchema
})
    .strict();
/**
 * B5-W5 prior-decision anchoring for infrastructure: compact material
 * selection rows from a previously ACCEPTED decision. This mirrors the
 * decision-hash infrastructure surface rather than preserving prose.
 */
export const PriorInfrastructureSelectionSummarySchema = z
    .object({
    itemKind: NonEmptyStringSchema,
    productPartNo: NonEmptyStringSchema.optional(),
    quantity: PositiveIntegerSchema,
    selectionStatus: z
        .enum(["selected", "covered_by_existing", "placeholder"])
        .optional(),
    provisionScope: z.enum(["apartment_type", "project"]).optional()
})
    .strict();
/**
 * B5-W3/B5-W5 prior-decision anchor: the previously ACCEPTED controller and
 * infrastructure decision tuple for one apartment type, supplied as prompt
 * CONTEXT on regeneration. The agent still decides — this is an instruction to
 * keep the prior decision unless verifier findings, source evidence, or a
 * policy change require a different one, never a forced/injected outcome.
 */
export const PriorAcceptedDecisionAnchorSchema = z
    .object({
    apartmentTypeId: IdSchema,
    sourceRunId: IdSchema.optional(),
    sourceRunIndex: NonNegativeIntegerSchema.optional(),
    sourceJobId: IdSchema.optional(),
    sourceProposalId: IdSchema.optional(),
    decisionHash: NonEmptyStringSchema.optional(),
    controllerDecisionHash: NonEmptyStringSchema.optional(),
    infrastructureDecisionHash: NonEmptyStringSchema.optional(),
    inputFingerprint: NonEmptyStringSchema.optional(),
    configFingerprint: NonEmptyStringSchema.optional(),
    providerCallArchitecture: NonEmptyStringSchema.optional(),
    controllers: z.array(PriorControllerAllocationSummarySchema),
    infrastructure: z.array(PriorInfrastructureSelectionSummarySchema).optional(),
    acceptedAt: IsoTimestampSchema.optional()
})
    .strict();
export const AgentDecisionAnchorStalenessSchema = z.enum([
    "not_present",
    "fresh",
    "stale",
    "incomparable"
]);
export const AgentDecisionAnchorProvenanceSchema = z
    .object({
    present: z.boolean(),
    staleness: AgentDecisionAnchorStalenessSchema,
    stale: z.boolean(),
    anchorFingerprint: NonEmptyStringSchema.optional(),
    anchorApartmentTypeId: IdSchema.optional(),
    sourceRunId: IdSchema.optional(),
    sourceRunIndex: NonNegativeIntegerSchema.optional(),
    sourceJobId: IdSchema.optional(),
    sourceProposalId: IdSchema.optional(),
    decisionHash: NonEmptyStringSchema.optional(),
    controllerDecisionHash: NonEmptyStringSchema.optional(),
    infrastructureDecisionHash: NonEmptyStringSchema.optional(),
    inputFingerprint: NonEmptyStringSchema.optional(),
    currentInputFingerprint: NonEmptyStringSchema.optional(),
    configFingerprint: NonEmptyStringSchema.optional(),
    currentConfigFingerprint: NonEmptyStringSchema.optional(),
    providerCallArchitecture: NonEmptyStringSchema.optional(),
    currentProviderCallArchitecture: NonEmptyStringSchema.optional(),
    message: NonEmptyStringSchema.optional()
})
    .strict();
/**
 * B5-W3 structured change explanation: required shape (not free prose) for
 * explaining a controller allocation that changed relative to the anchored
 * prior decision. `verifierFindingId`/`sourceRefs`/`ruleTraceRefs` are
 * deliberately optional at the schema level — an absent reference is a
 * VERIFIER FLAG (see `agent.unexplained_anchor_change` in specAgent.ts), not a
 * schema rejection, so the AI still decides and a missing reference never
 * blocks emission.
 */
export const ControllerAllocationChangeReasonKindSchema = z.enum([
    "verifier_finding",
    "source_evidence",
    "policy_change",
    "no_prior_anchor"
]);
export const ControllerAllocationChangeFromAnchorSchema = z
    .object({
    id: IdSchema,
    allocationId: IdSchema.optional(),
    changeSummary: NonEmptyStringSchema,
    priorRole: ControllerAllocationProposedRoleSchema.optional(),
    priorProductFamily: NonEmptyStringSchema.optional(),
    priorQuantity: PositiveIntegerSchema.optional(),
    newRole: ControllerAllocationProposedRoleSchema.optional(),
    newProductFamily: NonEmptyStringSchema.optional(),
    newQuantity: PositiveIntegerSchema.optional(),
    reasonKind: ControllerAllocationChangeReasonKindSchema,
    verifierFindingId: IdSchema.optional(),
    sourceRefs: z.array(SourceRefSchema).optional(),
    ruleTraceRefs: z.array(RuleTraceRefSchema).optional()
})
    .strict();
export const InfrastructureChangeFromAnchorSchema = z
    .object({
    id: IdSchema,
    itemId: IdSchema.optional(),
    changeSummary: NonEmptyStringSchema,
    priorItemKind: NonEmptyStringSchema.optional(),
    priorProductPartNo: NonEmptyStringSchema.optional(),
    priorQuantity: PositiveIntegerSchema.optional(),
    newItemKind: NonEmptyStringSchema.optional(),
    newProductPartNo: NonEmptyStringSchema.optional(),
    newQuantity: PositiveIntegerSchema.optional(),
    reasonKind: ControllerAllocationChangeReasonKindSchema,
    verifierFindingId: IdSchema.optional(),
    sourceRefs: z.array(SourceRefSchema).optional(),
    ruleTraceRefs: z.array(RuleTraceRefSchema).optional()
})
    .strict();
export const ControllerAllocationProposalItemSchema = z
    .object({
    id: IdSchema,
    logicalZoneIds: z.array(IdSchema).min(1),
    thermalLoadIds: z.array(IdSchema),
    pointDemandIds: z.array(IdSchema).optional(),
    proposedProductFamily: NonEmptyStringSchema,
    proposedProductPartNo: NonEmptyStringSchema.optional(),
    proposedRole: ControllerAllocationProposedRoleSchema,
    quantity: PositiveIntegerSchema,
    zoneSlots: z.array(ZoneSlotAssignmentSchema).optional(),
    expectedPointDemand: PointVectorSchema.optional(),
    rationale: AgentProposalRationaleSchema
})
    .strict();
export const ControllerAllocationProposalSchema = z
    .object({
    id: IdSchema,
    projectId: IdSchema,
    apartmentTypeId: IdSchema,
    strategyProposalId: IdSchema.optional(),
    allocations: z.array(ControllerAllocationProposalItemSchema).min(1),
    /**
     * B5-W3: structured explanation of any controller count/role change
     * relative to an anchored prior decision (see
     * {@link PriorAcceptedDecisionAnchorSchema}). Optional and additive —
     * absent when there is no anchor or nothing changed.
     */
    changesFromPriorDecision: z
        .array(ControllerAllocationChangeFromAnchorSchema)
        .optional(),
    createdByJobId: IdSchema.optional(),
    proposedAt: IsoTimestampSchema.optional()
})
    .strict();
export const InfrastructureProposalItemSchema = z
    .object({
    id: IdSchema,
    itemKind: NonEmptyStringSchema,
    proposedProductFamily: NonEmptyStringSchema.optional(),
    proposedProductPartNo: NonEmptyStringSchema.optional(),
    quantity: PositiveIntegerSchema,
    provisionScope: z.enum(["apartment_type", "project"]).optional(),
    provisionScopeBasis: MaterialFieldBasisSchema.optional(),
    installIncluded: z.boolean().optional(),
    installIncludedBasis: MaterialFieldBasisSchema.optional(),
    triggeringRequirementIds: z.array(IdSchema).min(1),
    protocols: z.array(NonEmptyStringSchema).optional(),
    connectivity: NonEmptyStringSchema.optional(),
    metering: MeteringInfrastructureDetailSchema.optional(),
    rationale: AgentProposalRationaleSchema
})
    .strict();
export const InfrastructureProposalSchema = z
    .object({
    id: IdSchema,
    projectId: IdSchema,
    apartmentTypeId: IdSchema.optional(),
    strategyProposalId: IdSchema.optional(),
    items: z.array(InfrastructureProposalItemSchema).min(1),
    /**
     * B5-W5: structured explanation of any infrastructure material/quantity
     * change relative to an anchored prior decision. Optional for normal runs;
     * certification blocks unexplained anchor changes at the harness layer.
     */
    changesFromPriorDecision: z.array(InfrastructureChangeFromAnchorSchema).optional(),
    createdByJobId: IdSchema.optional(),
    proposedAt: IsoTimestampSchema.optional()
})
    .strict();
export const RiskTagAnnotationSchema = z
    .object({
    id: IdSchema,
    targetKind: z.literal("risk"),
    targetId: IdSchema,
    tags: RiskTagsObjectSchema
})
    .strict();
export const ScopeTagAnnotationSchema = z
    .object({
    id: IdSchema,
    targetKind: z.literal("scope"),
    targetId: IdSchema,
    tags: ScopeTagsObjectSchema
})
    .strict();
export const SpecTagAnnotationSchema = z.discriminatedUnion("targetKind", [
    RiskTagAnnotationSchema,
    ScopeTagAnnotationSchema
]);
export const SpecDecisionProposalKindSchema = z.enum([
    "strategy",
    "controller_allocation",
    "infrastructure",
    "composed"
]);
export const SpecDecisionProposalSchema = z
    .object({
    id: IdSchema,
    projectId: IdSchema,
    proposalKind: SpecDecisionProposalKindSchema,
    iteration: PositiveIntegerSchema,
    strategyProposal: StrategyProposalSchema.optional(),
    controllerAllocationProposal: ControllerAllocationProposalSchema.optional(),
    infrastructureProposal: InfrastructureProposalSchema.optional(),
    tagAnnotations: z.array(SpecTagAnnotationSchema).optional(),
    createdByJobId: IdSchema.optional(),
    proposedAt: IsoTimestampSchema.optional()
})
    .strict()
    .superRefine((value, ctx) => {
    const hasStrategy = value.strategyProposal !== undefined;
    const hasControllerAllocation = value.controllerAllocationProposal !== undefined;
    const hasInfrastructure = value.infrastructureProposal !== undefined;
    const hasAnyProposal = hasStrategy || hasControllerAllocation || hasInfrastructure;
    if (!hasAnyProposal) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "spec decision proposal must wrap at least one proposal",
            path: ["proposalKind"]
        });
    }
    if (value.proposalKind === "strategy" && !hasStrategy) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "strategy proposal kind requires strategyProposal",
            path: ["strategyProposal"]
        });
    }
    if (value.proposalKind === "controller_allocation" && !hasControllerAllocation) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "controller_allocation proposal kind requires controllerAllocationProposal",
            path: ["controllerAllocationProposal"]
        });
    }
    if (value.proposalKind === "infrastructure" && !hasInfrastructure) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "infrastructure proposal kind requires infrastructureProposal",
            path: ["infrastructureProposal"]
        });
    }
});
export const VerifierCheckKindSchema = z.enum([
    "point_capacity",
    "controller_capacity",
    "catalog_review",
    "rule_invariant",
    "infrastructure_topology",
    "budget_policy",
    "other"
]);
export const VerifierFindingKindSchema = z.enum([
    "capacity_over_subscription",
    "unknown_product",
    "unreviewed_product",
    "rule_invariant_breach",
    "missing_requirement_trace",
    "budget_limit",
    "runtime_error",
    "other"
]);
export const VerifierSeveritySchema = z.enum(["info", "warning", "error", "blocker"]);
export const VerifierAnchorChangeDimensionSchema = z.enum([
    "quantity",
    "product",
    "role",
    "kind"
]);
export const VerifierAnchorChangeDemandSchema = z
    .object({
    required: z.literal(true),
    dimensions: z.array(VerifierAnchorChangeDimensionSchema).min(1)
})
    .strict();
export const VerifierFindingSchema = requireSourceOrRuleRef(z
    .object({
    id: IdSchema,
    kind: VerifierFindingKindSchema,
    severity: VerifierSeveritySchema,
    code: NonEmptyStringSchema,
    subjectRef: IdSchema.optional(),
    message: NonEmptyStringSchema,
    details: UnknownRecordSchema,
    anchorChangeDemand: VerifierAnchorChangeDemandSchema.optional(),
    sourceRefs: z.array(SourceRefSchema),
    ruleTraceRefs: z.array(RuleTraceRefSchema)
})
    .strict());
export const VerifierResultSchema = z
    .object({
    id: IdSchema,
    proposalId: IdSchema,
    proposalKind: SpecDecisionProposalKindSchema,
    status: z.enum(["ok", "violation", "advisory"]),
    checkKind: VerifierCheckKindSchema,
    severity: VerifierSeveritySchema,
    message: NonEmptyStringSchema,
    findings: z.array(VerifierFindingSchema),
    toolRequestId: IdSchema.optional(),
    toolResponse: EngineToolResponseSchema.optional(),
    checkedAt: IsoTimestampSchema
})
    .strict()
    .superRefine((value, ctx) => {
    if (value.status === "violation" && value.findings.length === 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "violating verifier results require at least one finding",
            path: ["findings"]
        });
    }
});
export const DecisionConvergenceVerifierOutcomeSchema = z
    .object({
    resultId: IdSchema,
    status: z.enum(["ok", "violation", "advisory"]),
    checkKind: VerifierCheckKindSchema,
    severity: VerifierSeveritySchema,
    message: NonEmptyStringSchema,
    findingCodes: z.array(NonEmptyStringSchema),
    sourceRefs: z.array(SourceRefSchema),
    ruleTraceRefs: z.array(RuleTraceRefSchema)
})
    .strict();
export const DecisionConvergenceModeSchema = z.enum([
    "verifier_guided_decision_variant",
    "global_aggregator_outlier"
]);
/**
 * B5-W4 verifier-guided convergence: context supplied for a second AI pass
 * when an N-run harness sees a different accepted decision variant. This is
 * evidence and anchor context only; it is not an injected decision and it does
 * not force the model to keep either variant.
 *
 * Agentic chain-3 adds the global_aggregator_outlier mode. The aggregator still
 * decides which apartment types are outliers and which sibling IDs to cite, but
 * the next apartment-agent pass receives a grounded template rendered from
 * validated sibling IDs, current-run sibling tool machine summaries, and the
 * outlier's own tool facts. Aggregator prose remains trace-only.
 */
export const DecisionConvergenceContextSchema = z
    .object({
    mode: DecisionConvergenceModeSchema,
    apartmentTypeId: IdSchema,
    priorRunIndex: NonNegativeIntegerSchema,
    currentRunIndex: NonNegativeIntegerSchema,
    priorDecisionHash: NonEmptyStringSchema,
    currentDecisionHash: NonEmptyStringSchema,
    priorControllerDecisionHash: NonEmptyStringSchema.optional(),
    currentControllerDecisionHash: NonEmptyStringSchema.optional(),
    priorVerifierOutcomes: z.array(DecisionConvergenceVerifierOutcomeSchema),
    currentVerifierOutcomes: z.array(DecisionConvergenceVerifierOutcomeSchema),
    instruction: NonEmptyStringSchema,
    globalRoundIndex: PositiveIntegerSchema.optional(),
    outlierReasons: z.array(NonEmptyStringSchema).optional(),
    siblingPatternCitation: z
        .object({
        apartmentTypeIds: z.array(IdSchema).min(1),
        summary: NonEmptyStringSchema
    })
        .strict()
        .optional()
})
    .strict();
export const DecisionConvergenceProvenanceSchema = z
    .object({
    present: z.boolean(),
    contextFingerprint: NonEmptyStringSchema.optional(),
    mode: DecisionConvergenceModeSchema.optional(),
    apartmentTypeId: IdSchema.optional(),
    priorRunIndex: NonNegativeIntegerSchema.optional(),
    currentRunIndex: NonNegativeIntegerSchema.optional(),
    priorDecisionHash: NonEmptyStringSchema.optional(),
    currentDecisionHash: NonEmptyStringSchema.optional(),
    globalRoundIndex: PositiveIntegerSchema.optional(),
    priorVerifierOutcomeCount: z.number().int().nonnegative(),
    currentVerifierOutcomeCount: z.number().int().nonnegative(),
    message: NonEmptyStringSchema.optional()
})
    .strict();
export const AgentGlobalAggregatorVerdictKindSchema = z.enum([
    "consistent",
    "outlier",
    "blocked"
]);
export const AgentGlobalAggregatorSiblingPatternCitationSchema = z
    .object({
    apartmentTypeIds: z.array(IdSchema).min(1),
    summary: NonEmptyStringSchema
})
    .strict();
export const AgentGlobalAggregatorVerdictSchema = z
    .object({
    apartmentTypeId: IdSchema,
    verdict: AgentGlobalAggregatorVerdictKindSchema,
    reasons: z.array(NonEmptyStringSchema),
    siblingPatternCitation: AgentGlobalAggregatorSiblingPatternCitationSchema.optional(),
    crossPollinationRecheckFragment: NonEmptyStringSchema.optional()
})
    .strict()
    .superRefine((value, ctx) => {
    if (value.verdict === "outlier") {
        if (value.reasons.length === 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "outlier verdicts require reasons",
                path: ["reasons"]
            });
        }
        if (!value.siblingPatternCitation) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "outlier verdicts require a same-project sibling pattern citation",
                path: ["siblingPatternCitation"]
            });
        }
        if (!value.crossPollinationRecheckFragment) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "outlier verdicts require a cross-pollination re-check fragment",
                path: ["crossPollinationRecheckFragment"]
            });
        }
    }
});
export const AgentGlobalAggregatorReviewSchema = z
    .object({
    summary: NonEmptyStringSchema,
    verdicts: z.array(AgentGlobalAggregatorVerdictSchema).min(1)
})
    .strict();
export const AgentGlobalAggregatorRecheckOutcomeSchema = z
    .object({
    apartmentTypeId: IdSchema,
    status: z.enum(["verified", "failed", "blocked"]),
    beforeSolutionHash: NonEmptyStringSchema.optional(),
    afterSolutionHash: NonEmptyStringSchema.optional(),
    traceId: IdSchema.optional(),
    iterationCount: PositiveIntegerSchema.optional(),
    message: NonEmptyStringSchema
})
    .strict();
/**
 * Stable, machine-readable codes for a global-aggregator review that could not
 * be used in full.
 *
 * These exist because a provider response can arrive structurally incomplete —
 * most often the provider hit its output-token budget mid-array and the
 * tolerant JSON salvage closed the truncated tail, leaving the final verdict
 * object without its required fields. Such a verdict is NEVER repaired,
 * guessed at, or silently dropped: it is recorded under one of these codes and
 * the affected apartment type is carried forward as a non-certifying
 * user-decision blocker, while every well-formed sibling verdict is honoured
 * exactly as it would have been.
 */
export const AGENT_GLOBAL_AGGREGATOR_DEGRADATION_CODES = [
    /** The verdict object was cut off by provider output truncation. */
    "global_aggregator_verdict_truncated",
    /** The verdict object was present and complete-looking but failed schema validation. */
    "global_aggregator_verdict_schema_incomplete",
    /** No usable review could be recovered from the response at all. */
    "global_aggregator_review_unusable"
];
export const AgentGlobalAggregatorDegradationCodeSchema = z.enum(AGENT_GLOBAL_AGGREGATOR_DEGRADATION_CODES);
export const AgentGlobalAggregatorDegradedVerdictSchema = z
    .object({
    code: AgentGlobalAggregatorDegradationCodeSchema,
    /** Position in the provider's verdict array, when the array itself parsed. */
    verdictIndex: NonNegativeIntegerSchema.optional(),
    /**
     * Only present when the malformed element still carried a usable
     * apartmentTypeId. Never inferred from position or from siblings.
     */
    apartmentTypeId: IdSchema.optional(),
    /** Schema paths that were absent or invalid, verbatim from validation. */
    invalidFieldPaths: z.array(NonEmptyStringSchema),
    detail: NonEmptyStringSchema
})
    .strict();
export const AgentGlobalAggregatorDegradationSchema = z
    .object({
    code: AgentGlobalAggregatorDegradationCodeSchema,
    roundIndex: PositiveIntegerSchema,
    /** True when the provider response was detected as truncated mid-structure. */
    providerOutputTruncated: z.boolean(),
    /** Schema-valid verdicts recovered from the same response and honoured normally. */
    salvagedVerdictCount: NonNegativeIntegerSchema,
    degradedVerdicts: z.array(AgentGlobalAggregatorDegradedVerdictSchema),
    detail: NonEmptyStringSchema
})
    .strict();
export const AgentGlobalAggregatorRoundTraceSchema = z
    .object({
    roundIndex: PositiveIntegerSchema,
    providerId: NonEmptyStringSchema,
    status: z.enum(["all_consistent", "outliers_rechecked", "blocked"]),
    summary: NonEmptyStringSchema,
    verdicts: z.array(AgentGlobalAggregatorVerdictSchema).min(1),
    rawReview: AgentGlobalAggregatorReviewSchema.optional(),
    postRecheckSummary: NonEmptyStringSchema.optional(),
    postRecheckVerdicts: z.array(AgentGlobalAggregatorVerdictSchema).optional(),
    postRecheckRawReview: AgentGlobalAggregatorReviewSchema.optional(),
    outlierApartmentTypeIds: z.array(IdSchema),
    recheckOutcomes: z.array(AgentGlobalAggregatorRecheckOutcomeSchema),
    degradation: AgentGlobalAggregatorDegradationSchema.optional(),
    startedAt: IsoTimestampSchema.optional(),
    completedAt: IsoTimestampSchema.optional()
})
    .strict();
export const AgentGlobalAggregatorTraceSchema = z
    .object({
    enabled: z.boolean(),
    providerId: NonEmptyStringSchema.optional(),
    maxRounds: PositiveIntegerSchema,
    finalState: z.enum([
        "skipped",
        "all_consistent",
        "converged",
        "blocked",
        "errored"
    ]),
    rounds: z.array(AgentGlobalAggregatorRoundTraceSchema),
    blockerApartmentTypeIds: z.array(IdSchema),
    /**
     * Every degradation observed across the run's aggregator rounds. Absent
     * when the aggregator produced fully well-formed reviews, so a clean run
     * writes a byte-identical trace to the pre-fix engine.
     */
    degradations: z.array(AgentGlobalAggregatorDegradationSchema).optional(),
    message: NonEmptyStringSchema
})
    .strict();
export const UserDecisionOptionSchema = z
    .object({
    id: IdSchema,
    label: NonEmptyStringSchema,
    disposition: NonEmptyStringSchema,
    rationale: NonEmptyStringSchema,
    sourceRefs: z.array(SourceRefSchema).optional(),
    ruleTraceRefs: z.array(RuleTraceRefSchema).optional()
})
    .strict();
export const UserDecisionRecordSchema = z
    .object({
    id: IdSchema,
    projectId: IdSchema.optional(),
    questionId: IdSchema,
    questionText: NonEmptyStringSchema,
    options: z.array(UserDecisionOptionSchema).min(1),
    chosenDisposition: NonEmptyStringSchema,
    decidedBy: z.enum(["human", "agent", "default"]),
    status: z.enum(["open", "answered", "applied"]),
    affectedRequirementIds: z.array(IdSchema),
    affectedRuleCardIds: z.array(IdSchema),
    affectedRuleTraceRefs: z.array(RuleTraceRefSchema),
    sourceRefs: z.array(SourceRefSchema).min(1),
    rationale: NonEmptyStringSchema,
    decidedAt: IsoTimestampSchema.optional(),
    appliedAt: IsoTimestampSchema.optional()
})
    .strict()
    .superRefine((value, ctx) => {
    const knownDispositions = new Set(value.options.map((option) => option.disposition));
    if (!knownDispositions.has(value.chosenDisposition)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "chosenDisposition must match one of the decision options",
            path: ["chosenDisposition"]
        });
    }
    if (value.affectedRequirementIds.length +
        value.affectedRuleCardIds.length +
        value.affectedRuleTraceRefs.length ===
        0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "user decisions require at least one affected requirement or rule",
            path: ["affectedRequirementIds"]
        });
    }
});
export const AgentSpecLoopStepKindSchema = z.enum([
    "propose",
    "tool_call",
    "verify",
    "revise",
    "emit"
]);
export const ProviderFormatRetryFailureSchema = z
    .object({
    attempt: PositiveIntegerSchema,
    classifiedKind: z.enum([
        "JSON_PARSE",
        "SCHEMA_VALIDATION",
        "OUTPUT_TRUNCATED"
    ]),
    retryable: z.boolean(),
    message: NonEmptyStringSchema,
    rawTextByteLength: NonNegativeIntegerSchema.optional()
})
    .strict();
export const ProviderFormatRetryBackoffSchema = z
    .object({
    afterAttempt: PositiveIntegerSchema,
    delayMs: NonNegativeIntegerSchema
})
    .strict();
export const ProviderFormatRetryTraceSchema = z
    .object({
    callId: NonEmptyStringSchema,
    maxAttempts: PositiveIntegerSchema,
    attemptCount: PositiveIntegerSchema,
    recovered: z.boolean(),
    exhausted: z.boolean().optional(),
    failures: z.array(ProviderFormatRetryFailureSchema).optional(),
    backoffs: z.array(ProviderFormatRetryBackoffSchema).optional()
})
    .strict();
export const AgentSpecLoopTraceStepSchema = z
    .object({
    id: IdSchema,
    sequence: NonNegativeIntegerSchema,
    kind: AgentSpecLoopStepKindSchema,
    summary: NonEmptyStringSchema,
    jobStepId: IdSchema.optional(),
    jobStep: JobStepSchema.optional(),
    toolCallTraceIds: z.array(IdSchema),
    toolCalls: z.array(ToolCallTraceSchema).optional(),
    proposalIds: z.array(IdSchema),
    verifierResultIds: z.array(IdSchema),
    userDecisionRecordIds: z.array(IdSchema),
    providerRequest: ProviderRequestFingerprintSchema.optional(),
    /**
     * Schema-wall-fix (two-call proposal composition): a "propose" step may
     * now be backed by more than one underlying Gemini call (call1-core:
     * strategy+controller; call2-ext: infrastructure+tags). `providerRequest`
     * above stays the primary/call1 fingerprint for backward compatibility;
     * this plural field carries every call's fingerprint in call order so
     * traces/ledger evidence is never lost for the second call. Optional and
     * additive — absent for single-call provider paths (mock, overseer).
     */
    providerRequests: z.array(ProviderRequestFingerprintSchema).optional(),
    /**
     * Per-call provider-format retry evidence. These attempts are JSON/schema
     * hygiene retries performed inside one gated provider call; they are not
     * domain verifier repairs and do not consume the apartment iteration budget.
     */
    providerFormatRetries: z.array(ProviderFormatRetryTraceSchema).optional(),
    /**
     * Agentic chain-2: compact per-apartment agent-tool evidence captured
     * before the provider's proposal is accepted. Additive and optional so
     * persisted pre-chain-2 traces continue to parse unchanged.
     */
    agentToolResults: z.array(UnknownRecordSchema).optional(),
    agentToolViolationCount: NonNegativeIntegerSchema.optional(),
    agentToolRecheckPrompt: NonEmptyStringSchema.optional(),
    startedAt: IsoTimestampSchema.optional(),
    completedAt: IsoTimestampSchema.optional()
})
    .strict();
export const VerifiedSolutionRefSchema = z
    .object({
    kind: z.enum(["apartment_solution", "spec_snapshot", "spec_run"]),
    id: IdSchema,
    apartmentTypeId: IdSchema.optional(),
    solutionHash: NonEmptyStringSchema.optional()
})
    .strict();
/**
 * Track 1 (graceful best-effort completion): separates CERTIFICATION from
 * USABILITY. A `draft` trace carries a real, materialized apartment solution
 * that the engine could emit but that did NOT clear every check inside the
 * bounded repair loop. It is usable — it can be viewed, edited, downloaded and
 * taken off — but it is explicitly not certified and must never be issued to a
 * client as if it were.
 */
export const AgentSpecCertificationStatusSchema = z.enum([
    "certified",
    "not_certified"
]);
/** A draft is always a retained schema-valid live-provider candidate. */
export const AgentSpecDraftProvenanceSchema = z.enum([
    "gemini_candidate",
    "openai_candidate"
]);
export const AgentSpecLoopTraceSchema = z
    .object({
    id: IdSchema,
    orgId: IdSchema,
    projectId: IdSchema,
    apartmentTypeId: IdSchema.optional(),
    specRunId: IdSchema.optional(),
    agentJobId: IdSchema,
    agentJob: AgentJobSchema.optional(),
    status: z.enum(["verified", "blocked", "draft", "failed"]),
    iterationCount: PositiveIntegerSchema,
    steps: z.array(AgentSpecLoopTraceStepSchema).min(1),
    proposals: z.array(SpecDecisionProposalSchema),
    policyFlagProposal: SpecPolicyFlagProposalSchema.optional(),
    policyFlagSet: SpecPolicyFlagSetSchema.optional(),
    verifierResults: z.array(VerifierResultSchema),
    needsUserDecisionItems: z.array(UserDecisionRecordSchema),
    /**
     * Present for every certified outcome (`verified`/`blocked`). NEVER
     * populated for a `draft`: a draft solution is materialized but not
     * verified, and calling it verified is exactly the dishonesty this
     * contract exists to prevent.
     */
    finalVerifiedSolutionRef: VerifiedSolutionRefSchema.optional(),
    /** Required for `draft`: the materialized-but-not-certified solution. */
    finalCandidateSolutionRef: VerifiedSolutionRefSchema.optional(),
    certificationStatus: AgentSpecCertificationStatusSchema.optional(),
    /** The checks that were still unresolved when the draft was materialized. */
    unresolvedFindings: z.array(VerifierFindingSchema).optional(),
    draftProvenance: AgentSpecDraftProvenanceSchema.optional(),
    anchorProvenance: AgentDecisionAnchorProvenanceSchema.optional(),
    convergenceProvenance: DecisionConvergenceProvenanceSchema.optional(),
    startedAt: IsoTimestampSchema,
    completedAt: IsoTimestampSchema.optional()
})
    .strict()
    .superRefine((value, ctx) => {
    for (let index = 1; index < value.steps.length; index += 1) {
        const previous = value.steps[index - 1];
        const current = value.steps[index];
        if (previous === undefined || current === undefined) {
            continue;
        }
        if (current.sequence <= previous.sequence) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "agent spec loop steps must be ordered by increasing sequence",
                path: ["steps", index, "sequence"]
            });
        }
    }
    const hasProposedOrDefaultedFlags = (value.policyFlagProposal?.proposedFlags.length ?? 0) +
        (value.policyFlagProposal?.defaultedFlags.length ?? 0) >
        0;
    if (hasProposedOrDefaultedFlags && value.policyFlagSet === undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "F2-aware traces with active policy flags require policyFlagSet",
            path: ["policyFlagSet"]
        });
    }
    // Track 1 discriminated outcome. Certification and usability are separate
    // facts and the schema refuses to let them be conflated in either
    // direction: a certified outcome must name a verified solution, and a
    // draft must name a candidate solution while explicitly declaring itself
    // not certified.
    if (value.status === "draft") {
        if (value.finalCandidateSolutionRef === undefined) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "draft traces require finalCandidateSolutionRef",
                path: ["finalCandidateSolutionRef"]
            });
        }
        if (value.finalVerifiedSolutionRef !== undefined) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "draft traces must not populate finalVerifiedSolutionRef; a draft solution is materialized, not verified",
                path: ["finalVerifiedSolutionRef"]
            });
        }
        if (value.certificationStatus !== "not_certified") {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'draft traces require certificationStatus "not_certified"',
                path: ["certificationStatus"]
            });
        }
        if (value.draftProvenance === undefined) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "draft traces require draftProvenance",
                path: ["draftProvenance"]
            });
        }
    }
    else {
        if (value.finalVerifiedSolutionRef === undefined) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "non-draft traces require finalVerifiedSolutionRef",
                path: ["finalVerifiedSolutionRef"]
            });
        }
        if (value.certificationStatus === "not_certified") {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'certificationStatus "not_certified" is reserved for draft traces',
                path: ["certificationStatus"]
            });
        }
        if (value.draftProvenance !== undefined) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "draftProvenance is reserved for draft traces",
                path: ["draftProvenance"]
            });
        }
    }
});
//# sourceMappingURL=agentSpec.js.map